import { Card } from "@/components/Card";
import React, { useState } from "react";
import Select from "@/components/Select"; 
import { applications } from "./Applications";
import IntegrationsModal from "./NetSuite/NetsuitePreviewModal";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import useApi from "@/hooks/useApi";
import { fetchNetSuiteConfig, fetchFormstackConfig } from "./IntegrationConfigs";
import { appCategories } from "./Categories";
import { IconProps } from "@velaro/velaro-component-lib";

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
}

interface FormstackConfig {
  displayName: string;
  token: string;
}

type App = {
  id: string;
  name: string;
  category: string;
  icon?: string | ((props: IconProps) => JSX.Element); // Accept both image paths and components
  subTitle: string;
};


interface OptionType {
  value: string | number;
  label: string;
}

const customSingleValue = ({ data }: { data: OptionType }) => (
  <div className="flex items-center gap-2">
    <img src="" alt="Selected" className="w-4 h-4" />
    {data.label}
  </div>
);

const customOption = (props: { data: any; isSelected: any; innerRef: any; innerProps: any; }) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
        isSelected ? "bg-blue-100" : "bg-white"
      }`}
    >
      {isSelected && <img src="" alt="Selected" className="w-4 h-4" />}
      {data.label}
    </div>
  );
};
export default function Landing() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [visibleCount, setVisibleCount] = useState(11);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);

  const [netSuiteConfigs, setNetSuiteConfigs] = useState<NetSuiteConfig[]>([]);
  const [formstackConfig, setFormstackConfig] = useState<FormstackConfig | null>(null);
  
  const api = useApi();
  const { teamId } = useSelectedTeam();

  const filteredApps =
    selectedCategory === "all"
      ? applications
      : applications.filter((app) => app.category === selectedCategory);

  React.useEffect(() => {
    setVisibleCount(11);
  }, [selectedCategory]);

  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + 6);
  };

  const handleCardClick = async (app: App) => {
    const hasConfig = checkIfConfigExists(app);
    if (hasConfig) {
      window.location.href = `/integrations/${app.id}`;
    } else {
      setSelectedApp(app);
      setShowIntegrationsModal(true);
    }
  };
  
  const checkIfConfigExists = (app: App): boolean => {
    if (app.id === "netsuite") {
      return netSuiteConfigs.length > 0; 
    } else if (app.id === "formstack") {
      return formstackConfig !== null;
    }
    return false;
  };
  
  React.useEffect(() => {
    async function loadConfigs() {
      if (teamId) {
        const netSuiteData = await fetchNetSuiteConfig(api, teamId);
        if (netSuiteData) setNetSuiteConfigs(netSuiteData);
      }
      const formstackData = await fetchFormstackConfig(api);
      if (formstackData) setFormstackConfig(formstackData);
    }
  
    loadConfigs();
  }, [api, teamId]);
  
  const handleModalClose = () => {
    setShowIntegrationsModal(false);
  };

  const modifiedCategories = appCategories.map((cat) => ({
    ...cat,
    label:
      cat.value === selectedCategory ? ` ${cat.label}` : cat.label, // Add check mark
  }));
  return (
    <div className="panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="text-center text-slate-800 text-lg font-medium font-['Rubik'] leading-7">All Apps
          <span className="text-center text-slate-500 text-lg font-medium font-['Rubik'] leading-7 ml-2">{filteredApps.length - 1 } apps</span>
        </div>
        <style>
          {`select option[value=""] { display: none; }`}
        </style>
        <Select
          className="w-48 h-12 text-sm px-3 py-2 border rounded-md !w-48"
          value={selectedCategory}
          options={modifiedCategories} // Use modified labels with check icon
          onChange={(value) => setSelectedCategory(value)}
        />
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 netsuite-card">
        {filteredApps.slice(0, visibleCount).map((app, index) => (
          <Card
            key={app.id}
            to=""
            title={app.name}
            subtitle={app.subTitle}
            icon={app.icon}
            variant="integrations"
            isFirstCard={index === 0}
            onClick={() => {
              console.log("Card clicked:", app);
              handleCardClick(app);
            }}
          />
        ))}
      </div>

      {visibleCount < filteredApps.length && (
        <div className="flex justify-center mt-4">
          <button
            onClick={handleLoadMore}
            className="text-blue-600 font-medium focus:outline-none"
          >
            Load More
          </button>
        </div>
      )}
      
      {showIntegrationsModal && selectedApp && (
        <IntegrationsModal
          show= {showIntegrationsModal}
          onClose={handleModalClose}
          appId={selectedApp.id}
          appName={selectedApp.name}
          icon={selectedApp.icon || ""}
        />
      )}
    </div>
  );
}
